import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, CheckCircle2, Circle, FileText, Hammer, Inbox, Plus, Receipt, Settings, Users, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { CountBadge, FxNote, OpenedBadge, getMoneyCurrency, money, fmtDate, setMoneyCurrency, StatusPill, usdOf, useNewLeads } from "@/lib/billing";
import { Card, PortalPage, Spinner } from "@/components/PortalPage";
import { NotificationBell } from "@/components/billing/Notifications";
import { CustomersTab } from "@/pages/billing/Customers";
import { LeadsTab } from "@/pages/billing/Leads";
import { toast } from "sonner";
import { PracticeBanner, TermsCard } from "@/components/RoyaltyTerms";
import { ImportReviewCard } from "@/components/billing/ImportReviewCard";

export const useBillingStatus = () => {
  const [st, setSt] = useState(null);
  const load = useCallback(() => api("/billing/status").then((s) => { setMoneyCurrency(s.currency); setSt(s); }).catch(() => setSt({ enabled: false })), []);
  useEffect(() => { load(); }, [load]);
  return [st, load];
};

export const NotEnabled = () => (
  <PortalPage eyebrow="INVOICING" title="Invoicing & Payments" testId="billing-not-enabled">
    <Card><p className="text-sm text-[#A0A0A0]">Invoicing isn't turned on for your account yet. ALR will let you know as soon as it's ready for you.</p></Card>
  </PortalPage>
);

const Step = ({ done, title, desc, to, testId }) => (
  <Link to={to} data-testid={testId} className="flex items-start gap-3 rounded-lg border border-[#333] p-3 transition-colors hover:border-[var(--accent)]">
    {done ? <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[var(--accent)]" /> : <Circle size={18} className="mt-0.5 shrink-0 text-[#666]" />}
    <div><p className="text-sm font-bold">{title}</p><p className="text-xs text-[#A0A0A0]">{desc}</p></div>
  </Link>
);

const Stat = ({ label, value, testId }) => (
  <Card className="min-w-[150px] flex-1"><p className="text-[10px] font-bold tracking-[2px] text-[#888]">{label}</p><p className="mt-1 text-xl font-extrabold" data-testid={testId}>{value}</p></Card>
);

const TABS = [["leads", "Leads", Inbox], ["invoice", "Invoices", Receipt], ["quote", "Quotes", FileText], ["jobs", "Jobs", Hammer], ["customers", "Customers", Users], ["royalties", "Royalties", Wallet]];

const DocList = ({ kind }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api(`/billing/docs?kind=${kind}`).then(setRows).catch(() => setRows([])); }, [kind]);
  if (!rows) return <Spinner />;
  if (!rows.length) return <p className="py-8 text-center text-sm text-[#888]" data-testid={`${kind}-empty`}>No {kind}s yet.</p>;
  return (
    <div className="divide-y divide-[#262626]" data-testid={`${kind}-list`}>
      {rows.map((d) => (
        <Link key={d.id} to={`/billing/doc/${d.id}`} data-testid={`${kind}-row-${d.number}`} className="flex items-center gap-3 py-3 hover:bg-[#151515]">
          <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold">#{d.number} · {d.customer_name}</p><p className="truncate text-xs text-[#888]">{d.title || "Untitled"} · {fmtDate(d.created_at)}</p>{d.views?.count > 0 && <div className="mt-1"><OpenedBadge views={d.views} testId={`${kind}-opened-${d.number}`} /></div>}</div>
          <div className="text-right"><p className="text-sm font-extrabold">{money(d.totals.total)}</p>{kind === "invoice" && d.totals.balance > 0 && d.totals.amount_paid > 0 && <p className="text-[11px] text-[#FFB800]">{money(d.totals.balance)} due</p>}</div>
          <StatusPill status={d.status} />
        </Link>
      ))}
    </div>
  );
};

const JobList = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api("/billing/jobs").then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return <Spinner />;
  if (!rows.length) return <p className="py-8 text-center text-sm text-[#888]" data-testid="jobs-empty">No jobs yet. Convert an approved quote or add one.</p>;
  return (
    <div className="divide-y divide-[#262626]" data-testid="jobs-list">
      {rows.map((j) => (
        <Link key={j.id} to={`/billing/job/${j.id}`} data-testid={`job-row-${j.number}`} className="flex items-center gap-3 py-3 hover:bg-[#151515]">
          <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold">#{j.number} · {j.title}</p><p className="truncate text-xs text-[#888]">{j.customer_name}{j.start_at ? ` · ${new Date(`${j.start_at}:00`).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : j.scheduled_date ? ` · ${fmtDate(j.scheduled_date)}` : ""}</p></div>
          <StatusPill status={j.status} />
        </Link>
      ))}
    </div>
  );
};

export const RoyaltyRows = ({ rows, actions }) => (
  <div className="divide-y divide-[#262626]" data-testid="royalty-list">
    {rows.map((e) => (
      <div key={e.id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`royalty-row-${e.id}`}>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">Invoice #{e.invoice_number} · {e.customer_name}</p>
          <p className="text-xs text-[#888]">{fmtDate(e.created_at)} · {e.reason} · on {money(e.base_amount, e.currency)} · royalty {money(e.royalty_amount, e.currency)} + marketing {money(e.marketing_amount, e.currency)}</p>
          {e.collected_at_checkout > 0 && <p className="text-xs text-[var(--accent)]" data-testid={`royalty-checkout-${e.id}`}>{money(e.collected_at_checkout, e.currency)} collected automatically at card checkout</p>}
          {e.credit_applied > 0 && <p className="text-xs text-[var(--accent)]">{money(e.credit_applied, e.currency)} covered by royalty credit</p>}
          {e.credit_applied < 0 && <p className="text-xs text-[#A0A0A0]">Includes {money(-e.credit_applied, e.currency)} of credit from this invoice that was already used on an earlier draft</p>}
          <FxNote e={e} testId={`royalty-fx-${e.id}`} />
          {e.nsf_fee > 0 && <p className="text-xs text-[#FFB800]" data-testid={`royalty-nsf-${e.id}`}>Plus {money(e.nsf_fee, "usd")} returned-payment fee (Franchise Agreement 4.7(d))</p>}
          {e.note?.startsWith("Practice") && <p className="text-xs text-[#A0A0A0]" data-testid={`royalty-note-${e.id}`}>{e.note}</p>}
          {e.status !== "paid_invoice" && (e.failure_reason || e.attention_reason) && <p className="text-xs text-[#ff8080]">{e.attention_reason || `Draft failed: ${e.failure_reason}`}</p>}
        </div>
        <p className="text-sm font-extrabold" data-testid={`royalty-amount-${e.id}`}>{money(usdOf(e, e.amount - (e.credit_applied || 0)), "usd")}</p>
        <StatusPill status={e.status} />
        {actions?.(e)}
      </div>
    ))}
  </div>
);

const monthName = (m) => new Date(`${m}-15`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

const TechFees = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api("/billing/tech-fees").then(setD).catch(() => setD(null)); }, []);
  if (!d) return null;
  return (
    <div className="mb-4 rounded-lg border border-[#333] p-3" data-testid="tech-fees">
      <p className="text-xs font-extrabold tracking-[1px]">TECHNOLOGY FEE</p>
      <p className="mt-1 text-xs text-[#A0A0A0]" data-testid="tech-fee-explainer">
        {d.exempt ? "Your location is exempt from the Technology Fee." : `${money(d.settings.amount_usd, "usd")} per month, drafted in USD from your connected bank on the 1st.`}
        {!d.exempt && getMoneyCurrency() === "cad" && " Your bank statement may show the CAD equivalent if the account converts."}
        {!d.exempt && !d.settings.enabled && " Collection hasn't started yet; ALR will let you know before the first draft."}
      </p>
      {d.rows.length > 0 && <div className="mt-2 divide-y divide-[#262626]">{d.rows.map((f) => (
        <div key={f.id} className="flex flex-wrap items-center gap-3 py-2" data-testid={`tech-fee-row-${f.id}`}>
          <div className="min-w-0 flex-1"><p className="text-sm font-bold">{monthName(f.month)}</p>
            {f.cad_equivalent && <p className="text-[11px] text-[#A0A0A0]">About {money(f.cad_equivalent, "cad")} at the Bank of Canada rate of {f.fx_cad_per_usd} CAD per USD ({f.fx_date})</p>}
            {f.failure_reason && f.status !== "paid_invoice" && <p className="text-[11px] text-[#ff8080]">Draft failed: {f.failure_reason}{f.nsf_fee ? ` · plus ${money(f.nsf_fee, "usd")} returned-payment fee` : ""}</p>}
          </div>
          <p className="text-sm font-extrabold">{money(f.amount_usd, "usd")}</p><StatusPill status={f.status} />
        </div>))}</div>}
    </div>
  );
};

const CurrencySummary = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api("/billing/currency-summary").then((r) => setRows(r.rows)).catch(() => setRows(null)); }, []);
  if (!rows) return null;
  return (
    <div className="mb-4 rounded-lg border border-[#333] p-3" data-testid="currency-summary">
      <p className="text-xs font-extrabold tracking-[1px]">CAD SALES VS USD ROYALTIES & FEES · LAST 6 MONTHS</p>
      <p className="mt-1 text-[11px] text-[#A0A0A0]">Sales are payments you collected in CAD. Royalties are converted at the Bank of Canada rate on each payment date, and everything ALR collects is in USD.</p>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] tracking-[1px] text-[#888]"><th className="py-1.5 pr-3">MONTH</th><th className="pr-3 text-right">SALES (CAD)</th><th className="pr-3 text-right">ROYALTY (CAD → USD)</th><th className="pr-3 text-right">TECH FEE</th><th className="pr-3 text-right">TOTAL PAID TO ALR</th><th className="text-right">AVG RATE</th></tr></thead>
          <tbody className="divide-y divide-[#262626]">
            {rows.map((r) => (
              <tr key={r.month} data-testid={`currency-row-${r.month}`}>
                <td className="py-1.5 pr-3 font-bold">{monthName(r.month)}</td>
                <td className="pr-3 text-right tabular-nums">{money(r.sales_cad, "cad")}</td>
                <td className="pr-3 text-right tabular-nums">{money(r.royalty_cad, "cad")} → <b>{money(r.royalty_usd, "usd")}</b></td>
                <td className="pr-3 text-right tabular-nums">{r.tech_fee_usd ? <>{money(r.tech_fee_usd, "usd")}{r.tech_fee_cad ? <span className="text-[#888]"> (≈{money(r.tech_fee_cad, "cad")})</span> : null}</> : "–"}</td>
                <td className="pr-3 text-right font-bold tabular-nums">{money(r.total_usd, "usd")}</td>
                <td className="text-right text-[#888]">{r.avg_rate ? `${r.avg_rate} CAD/USD` : "–"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Royalties = ({ practice, onChanged }) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api("/billing/royalties").then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Spinner />;
  const cad = getMoneyCurrency() === "cad";
  if (!rows.length) return <>{cad && <CurrencySummary />}<TechFees /><p className="py-8 text-center text-sm text-[#888]" data-testid="royalties-empty">Royalties appear here automatically when you collect a payment.</p></>;
  const sim = async (e, outcome) => {
    try { await api(`/billing/practice/ledger/${e.id}/${outcome}`, { method: "POST" }); toast.success(outcome === "fail" ? "Draft failed. Check your email for the notice." : "Draft cleared"); load(); onChanged?.(); } catch (err) { toast.error(err.message); }
  };
  return <>{cad && <CurrencySummary />}<TechFees /><RoyaltyRows rows={rows} actions={practice ? (e) => e.status === "processing" && (
    <div className="flex gap-2 text-[11px] font-bold">
      <button onClick={() => sim(e, "clear")} className="text-[var(--accent)]" data-testid={`practice-clear-${e.id}`}>Mark cleared</button>
      <button onClick={() => sim(e, "fail")} className="text-[#ff8080]" data-testid={`practice-fail-${e.id}`}>Make this draft fail</button>
    </div>) : undefined} /></>;
};

export default function BillingHome() {
  const [st, reloadSt] = useBillingStatus();
  const [sum, setSum] = useState(null);
  const newLeads = useNewLeads(!!st?.enabled);
  const [tab, setTab] = useState("invoice");
  const nav = useNavigate();
  const loadSum = useCallback(() => api("/billing/summary").then(setSum).catch(() => {}), []);
  useEffect(() => { if (st?.enabled) loadSum(); }, [st, loadSum]);
  if (!st) return <Spinner />;
  if (!st.enabled) return <NotEnabled />;
  const a = st.account || {};
  const ready = st.can_collect && a.connect_status === "active";
  const NEW = [["/billing/new/quote", "NEW QUOTE", "quote"], ["/billing/new/invoice", "NEW INVOICE", "invoice"], ["/billing/new-job", "NEW JOB", "job"]];
  return (
    <PortalPage eyebrow="INVOICING" title="Invoicing & Payments" width="max-w-5xl" testId="billing-home"
      actions={<div className="flex gap-2"><NotificationBell /><Link to="/billing/comms" data-testid="billing-comms-link" className="flex h-10 items-center gap-1.5 rounded-[10px] border border-[#333] px-3 text-xs font-bold">MESSAGES</Link><Link to="/billing/calendar" data-testid="billing-calendar-link" className="flex h-10 items-center gap-1.5 rounded-[10px] border border-[#333] px-3 text-xs font-bold"><CalendarDays size={16} />CALENDAR</Link><Link to="/billing/setup" data-testid="billing-setup-link" className="flex h-10 w-10 items-center justify-center rounded-[10px] border border-[#333]"><Settings size={18} /></Link></div>}>
      <PracticeBanner st={st} />
      <div className="flex flex-wrap gap-2" data-testid="billing-new-actions">
        {NEW.map(([to, label, k], i) => <button key={k} onClick={() => nav(to)} data-testid={`billing-new-${k}-btn`} className={`flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-xs font-extrabold transition-colors ${i === 0 ? "bg-[var(--accent)] text-black" : "border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10"}`}><Plus size={14} />{label}</button>)}
      </div>
      {!st.terms_accepted && a.bank_status === "active" && <TermsCard st={st} onAccepted={reloadSt} title="PLEASE REVIEW: HOW ROYALTY IS COLLECTED" />}
      {!ready && (
        <Card data-testid="billing-setup-checklist">
          <p className="mb-3 text-xs font-bold tracking-[2px] text-[var(--accent)]">GET SET UP</p>
          <div className="grid gap-2 md:grid-cols-3">
            <Step done={!!a.business_name} title="1. Business details" desc="Name and contact shown on your quotes" to="/billing/setup" testId="step-profile" />
            <Step done={st.can_collect} title="2. Connect bank for royalty" desc="Accept royalty terms and connect your bank. Required before you can collect payments" to="/billing/setup" testId="step-bank" />
            <Step done={a.connect_status === "active"} title="3. Accept cards" desc="Connect your Stripe account so customers can pay online" to="/billing/setup" testId="step-stripe" />
          </div>
        </Card>
      )}
      {sum && (
        <div className="flex flex-wrap gap-3">
          <Stat label="OUTSTANDING" value={money(sum.outstanding)} testId="stat-outstanding" />
          <Stat label="COLLECTED" value={money(sum.collected)} testId="stat-collected" />
          <Stat label="OPEN QUOTES" value={sum.quotes_open} testId="stat-quotes" />
          <Stat label="ROYALTY PAID" value={money(sum.royalty_paid, "usd")} testId="stat-royalty-paid" />
        </div>
      )}
      {sum?.royalty_attention > 0 && <Card className="border-[#FFB800]/60" data-testid="royalty-attention-banner"><p className="text-sm text-[#FFB800]">{money(sum.royalty_attention, "usd")} in royalty couldn't be drafted from your bank, so it's billed to you by QuickBooks invoice instead. Please pay it promptly to stay current with your Franchise Agreement.</p></Card>}
      <ImportReviewCard />
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`billing-tab-${k}`} className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold transition-colors ${tab === k ? "bg-[var(--accent)] text-black" : "border border-[#333] text-[#ccc] hover:border-[#555]"}`}><Icon size={14} />{l}{k === "leads" && <CountBadge n={newLeads} testId="billing-tab-leads-badge" />}</button>
          ))}
          <div className="flex-1" />
        </div>
        {tab === "leads" && <LeadsTab />}
        {tab === "invoice" && <DocList kind="invoice" />}
        {tab === "quote" && <DocList kind="quote" />}
        {tab === "jobs" && <JobList />}
        {tab === "customers" && <CustomersTab />}
        {tab === "royalties" && <Royalties practice={st.practice} onChanged={loadSum} />}
      </Card>
    </PortalPage>
  );
}
